/**
 * Represents detailed information about a technology used in a project.
 */
export interface ITech {
	/**
	 * Name of the technology.
	 */
	name: string;

	/**
	 * Lines of code written using this technology.
	 */
	linesOfCode: number;
}

/**
 * Represents a collection of technologies used in a project.
 * Each technology can be a simple string or a detailed ITech object.
 */
export type ITechnologies = (string | ITech)[];

/**
 * Represents a project with details about its name, description, associated technologies, 
 * dates, and relevant companies or universities.
 */
export interface IProject {
	/** The name of the project */
	name: string;

	/** A brief description of the project */
	description: string;

	/** The URL of the project (optional) */
	url?: string | string[];

	/**
	 * Technologies used in the project, either as simple strings or as 
	 * detailed objects containing the name and lines of code.
	 */
	technologies?: ITechnologies;

	/** Rating or category of the project (optional) */
	rating?: string;

	/** FontAwesome icon class for representing the project */
	fa_icon: string;

	/** Start date of the project in 'YYYY-MM-DD' format */
	start_date: string;

	/** End date of the project in 'YYYY-MM-DD' format (optional) */
	end_date?: string;

	/** The company that sponsored or led the project (optional) */
	company?: string;

	/** URL of the company's website (optional) */
	company_url?: string;

	/** The university associated with the project (optional) */
	university?: string;

	/** URL of the university's website (optional) */
	university_url?: string;

	/** Class or course related to the project, can be a list or single class (optional) */
	class?: string[] | string;

	/** URL(s) of the class(es) related to the project, can be a list or single URL (optional) */
	class_url?: string[] | string;

	/** Location where the project was carried out */
	location: string;
}


export interface ProjectRating {
	title: string;
	description: string;
	tooltip: string;
}

export const ProjectRatings: { [key: string]: ProjectRating } = {
	A: {
		title: "Outstanding",
		description: "This is one of the most <strong>interesting</strong> projects that I have worked on. I was able to <strong>learn</strong> a great deal from it whilst also being <strong>challenged to grow</strong> and <strong>improve my knowledge</strong> in order to complete it.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},
	B: {
		title: "Great",
		description: "This project was very <strong>educational</strong>, having <strong>improved my knowledge</strong> and <strong>furthered my experience</strong> in the technologies used.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},
	C: {
		title: "Good",
		description: "This project improved my knowledge in key areas that are useful to my interests.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},
	D: {
		title: "Fair",
		description: "This project somewhat helped refine my understanding of the technologies/libraries used in it.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},
	/*E: {
		title: "Basic",
		description: "This project, whilst educational, was not very <strong>challenging</strong> and was limited in the amount of benefits that I've gained from working on it.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},
	F: {
		title: "Minimal",
		description: "This project provided little to no value and is probably not even worth mentioning it here.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	},*/
	"?": {
		title: "Unrated",
		description: "This project is yet to be rated. I will update this rating once I have had the chance to review it.",
		get tooltip() {
			return `<p><strong>${this.title}</strong></p><p>${this.description}</p>`;
		}
	}
};